import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import captureSafeException from '../../../../util/sentry';
import ReviewListItems from './ReviewListItems';
import ReviewPaginationRenderer from './ReviewPaginationRenderer';

export default class ReviewPaginationHandler implements Observer {
    static #instance: ReviewPaginationHandler | null = null;

    static getInstance(): ReviewPaginationHandler {
        if (!ReviewPaginationHandler.#instance) {
            ReviewPaginationHandler.#instance = new ReviewPaginationHandler();
        }

        return ReviewPaginationHandler.#instance;
    }

    #paginationElement: HTMLElement | null = null;

    readonly #reviewPaginationRenderer: Promise<ReviewPaginationRenderer> =
        ReviewPaginationRenderer.getInstance();

    readonly #reviewListItems: ReviewListItems = ReviewListItems.getInstance();

    public constructor() {
        this.#reviewPaginationRenderer
            .then((renderer) => {
                renderer.attach(this);
            })
            .catch((err) => {
                captureSafeException(err, 'review_pagination_renderer_init_failed', null, {
                    region: 'ReviewPaginationHandler',
                    feature: 'reviews'
                });
            });
    }

    public update(subject: Subject): void {
        if (!(subject instanceof ReviewPaginationRenderer)) {
            return;
        }

        this.#paginationElement = subject.paginationElement;
        this.#setEventListeners();
    }

    #setEventListeners(): void {
        this.#paginationElement?.addEventListener('click', this.#handlePaginationClick);
    }

    readonly #handlePaginationClick = async (event: Event): Promise<void> => {
        event.preventDefault();

        if (!(event.target instanceof HTMLAnchorElement)) {
            return;
        }

        const url = new URL(event.target.href);
        const skip = Number(url.searchParams.get('skip')) || 0;

        const container = this.#paginationElement?.closest('[data-role="reviews-container"]');
        const firstReview = container?.querySelector('li');

        if (!firstReview || !container) {
            return;
        }

        container.toggleAttribute('data-loading', true);

        firstReview.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
        });

        try {
            await this.#reviewListItems.fetchReviews(skip);
        } catch (err) {
            captureSafeException(err, 'review_pagination_fetch_failed', null, {
                region: 'ReviewPaginationHandler',
                feature: 'reviews'
            });
        } finally {
            container.toggleAttribute('data-loading', false);
        }
    };
}
